import type { ToolRegistry } from '../../../core/tools/ToolRegistry';
import type { FrameManager } from '../../../core/frames/FrameManager';

import { QuickRelateTool } from '../../../domains/genogram/tools/QuickRelateTool';
import { AddParentsTool } from '../../../domains/genogram/tools/AddParentsTool';
import { GenogramColorTool } from '../../../domains/genogram/tools/GenogramColorTool';
import { GenogramTextTool } from '../../../domains/genogram/tools/GenogramTextTool';
import { GenogramShapeCreationTool } from '../../../domains/genogram/tools/GenogramShapeCreationTool';
import { GenogramDeleteTool } from '../../../domains/genogram/tools/GenogramDeleteTool';
import { GenogramConnectorTool } from '../../../domains/genogram/tools/GenogramConnectorTool';
import { GenogramMovementTool } from '../../../domains/genogram/tools/GenogramMovementTool';
import { GenogramSelectionTool } from '../../../domains/genogram/tools/GenogramSelectionTool';
import { GenogramFrameTool } from '../../../domains/genogram/tools/GenogramFrameTool';

import { ShapePickerTool } from '../../../tools/universal/ShapePickerTool';
import { ShapeNotesTool } from '../../../tools/universal/ShapeNotesTool';
import { StickyNoteTool } from '../../../tools/universal/StickyNoteTool';
import { StickyNotePropertiesTool } from '../../../tools/universal/StickyNotePropertiesTool';
import { ImagePropertiesTool } from '../../../tools/universal/ImagePropertiesTool';
import { PdfPropertiesTool } from '../../../tools/universal/PdfPropertiesTool';
import { TextTool } from '../../../tools/universal/TextTool';
import { CommentsTool } from '../../../tools/universal/CommentsTool';
import { FramesTool } from '../../../tools/universal/FramesTool';
import { PenTool } from '../../../tools/universal/PenTool';

import { DeleteTool } from '../../../tools/contextual/DeleteTool';
import { CopyTool } from '../../../tools/contextual/CopyTool';
import { ConnectTool } from '../../../tools/contextual/ConnectTool';
import { ShapeSwitchTool } from '../../../tools/contextual/ShapeSwitchTool';
import { GroupTool } from '../../../tools/contextual/GroupTool';
import { ShapeColorTool } from '../../../tools/contextual/ShapeColorTool';
import { ShapeOutlineTool } from '../../../tools/contextual/ShapeOutlineTool';
import { FontSizeTool } from '../../../tools/contextual/FontSizeTool';
import { FontPickerTool } from '../../../tools/contextual/FontPickerTool';
import { TextStyleTool } from '../../../tools/contextual/TextStyleTool';
import { TextAlignmentTool } from '../../../tools/contextual/TextAlignmentTool';
import { TextColorTool } from '../../../tools/contextual/TextColorTool';
import { ImageReplaceTool } from '../../../tools/contextual/ImageReplaceTool';
import { BpmnGatewayTypeTool } from '../../../tools/contextual/BpmnGatewayTypeTool';
import { BpmnEventStyleTool, BpmnEventTriggerTool } from '../../../tools/contextual/BpmnEventTools';
import { BpmnActivityStyleTool, BpmnActivityMarkerTool, BpmnActivityTaskTool } from '../../../tools/contextual/BpmnActivityTools';
import { TableGridTool } from '../../../tools/contextual/TableGridTool';
import { TableCellStyleTool } from '../../../tools/contextual/TableCellStyleTool';
import { LinkDiagramTool } from '../../../tools/contextual/LinkDiagramTool';

import { PersonStyleTool } from '../../../domains/genogram/tools/PersonStyleTool';
import { RelationshipEditTool } from '../../../domains/genogram/tools/RelationshipEditTool';
import { SchemaDrivenPersonTool } from '../../../domains/genogram/tools/SchemaDrivenPersonTool';
import { ResetPersonStyleTool } from '../../../domains/genogram/tools/ResetPersonStyleTool';
import { CultureTagTool } from '../../../domains/genogram/tools/CultureTagTool';
import { HealthTagTool } from '../../../domains/genogram/tools/HealthTagTool';
import { CulturePaletteTool } from '../../../domains/genogram/tools/CulturePaletteTool';
import { AddPersonTool } from '../../../domains/genogram/tools/AddPersonTool';
import { AddPartnerTool } from '../../../domains/genogram/tools/AddPartnerTool';
import { AddChildTool } from '../../../domains/genogram/tools/AddChildTool';
import { AddParentTool } from '../../../domains/genogram/tools/AddParentTool';
import { AddSiblingTool } from '../../../domains/genogram/tools/AddSiblingTool';
import { AddChildToCoupleTool } from '../../../domains/genogram/tools/AddChildToCoupleTool';
import { GroupAsSiblingsTool } from '../../../domains/genogram/tools/GroupAsSiblingsTool';
import { CreateRelationshipTool } from '../../../domains/genogram/tools/CreateRelationshipTool';

export type DefaultCanvasTools = {
  colorTool: GenogramColorTool;
  textTool: GenogramTextTool;
  shapeTool: GenogramShapeCreationTool;
  deleteTool: GenogramDeleteTool;
  connectorTool: GenogramConnectorTool;
  selectionTool: GenogramSelectionTool;
  movementTool: GenogramMovementTool;
  quickRelateTool: QuickRelateTool;
  addParentsTool: AddParentsTool;
  frameTool: GenogramFrameTool;

  shapePickerTool: ShapePickerTool;
  shapeNotesTool: ShapeNotesTool;
  stickyNoteTool: StickyNoteTool;
  stickyNotePropertiesTool: StickyNotePropertiesTool;
  imagePropertiesTool: ImagePropertiesTool;
  pdfPropertiesTool: PdfPropertiesTool;
  universalTextTool: TextTool;
  commentsTool: CommentsTool;
  framesTool: FramesTool;
  penTool: PenTool;

  universalDeleteTool: DeleteTool;
  copyTool: CopyTool;
  connectTool: ConnectTool;
  shapeSwitchTool: ShapeSwitchTool;
  groupTool: GroupTool;
  shapeColorTool: ShapeColorTool;
  shapeOutlineTool: ShapeOutlineTool;
  fontSizeTool: FontSizeTool;
  fontPickerTool: FontPickerTool;
  textStyleTool: TextStyleTool;
  textAlignmentTool: TextAlignmentTool;
  textColorTool: TextColorTool;
  imageReplaceTool: ImageReplaceTool;
  bpmnEventStyleTool: BpmnEventStyleTool;
  bpmnEventTriggerTool: BpmnEventTriggerTool;
  bpmnGatewayTypeTool: BpmnGatewayTypeTool;
  bpmnActivityStyleTool: BpmnActivityStyleTool;
  bpmnActivityMarkerTool: BpmnActivityMarkerTool;
  bpmnActivityTaskTool: BpmnActivityTaskTool;
  tableGridTool: TableGridTool;
  tableCellStyleTool: TableCellStyleTool;
  linkDiagramTool: LinkDiagramTool;

  personStyleTool: PersonStyleTool;
  relationshipEditTool: RelationshipEditTool;
  schemaDrivenPersonTool: SchemaDrivenPersonTool;
  resetPersonStyleTool: ResetPersonStyleTool;
  cultureTagTool: CultureTagTool;
  healthTagTool: HealthTagTool;
  culturePaletteTool: CulturePaletteTool;

  addPersonTool: AddPersonTool;
  addPartnerTool: AddPartnerTool;
  addChildTool: AddChildTool;
  addParentTool: AddParentTool;
  addSiblingTool: AddSiblingTool;
  addChildToCoupleTool: AddChildToCoupleTool;
  groupAsSiblingsTool: GroupAsSiblingsTool;
  createRelationshipTool: CreateRelationshipTool;
};

/**
 * Extracted from `DiagramCanvasWithTransform` as a first step towards Issue #998.
 * Keeps behavior the same while removing the long list of tool imports/initializers
 * from the canvas component.
 */
export class DefaultCanvasToolsPlugin {
  readonly tools: DefaultCanvasTools;
  private frameManager: FrameManager;

  constructor(frameManager: FrameManager) {
    this.frameManager = frameManager;

    const frameTool = new GenogramFrameTool();
    frameTool.setFrameManager(frameManager);

    this.tools = {
      colorTool: new GenogramColorTool(),
      textTool: new GenogramTextTool(),
      shapeTool: new GenogramShapeCreationTool(),
      deleteTool: new GenogramDeleteTool(),
      connectorTool: new GenogramConnectorTool(),
      selectionTool: new GenogramSelectionTool(),
      movementTool: new GenogramMovementTool(),
      quickRelateTool: new QuickRelateTool(),
      addParentsTool: new AddParentsTool(),
      frameTool,

      shapePickerTool: new ShapePickerTool(),
      shapeNotesTool: new ShapeNotesTool(),
      stickyNoteTool: new StickyNoteTool(),
      stickyNotePropertiesTool: new StickyNotePropertiesTool(),
      imagePropertiesTool: new ImagePropertiesTool(),
      pdfPropertiesTool: new PdfPropertiesTool(),
      universalTextTool: new TextTool(),
      commentsTool: new CommentsTool(),
      framesTool: new FramesTool(),
      penTool: new PenTool(),

      universalDeleteTool: new DeleteTool(frameManager),
      copyTool: new CopyTool(),
      connectTool: new ConnectTool(),
      shapeSwitchTool: new ShapeSwitchTool(),
      groupTool: new GroupTool(),
      shapeColorTool: new ShapeColorTool(),
      shapeOutlineTool: new ShapeOutlineTool(),
      fontSizeTool: new FontSizeTool(),
      fontPickerTool: new FontPickerTool(),
      textStyleTool: new TextStyleTool(),
      textAlignmentTool: new TextAlignmentTool(),
      textColorTool: new TextColorTool(),
      imageReplaceTool: new ImageReplaceTool(),
      bpmnEventStyleTool: new BpmnEventStyleTool(),
      bpmnEventTriggerTool: new BpmnEventTriggerTool(),
      bpmnGatewayTypeTool: new BpmnGatewayTypeTool(),
      bpmnActivityStyleTool: new BpmnActivityStyleTool(),
      bpmnActivityMarkerTool: new BpmnActivityMarkerTool(),
      bpmnActivityTaskTool: new BpmnActivityTaskTool(),
      tableGridTool: new TableGridTool(),
      tableCellStyleTool: new TableCellStyleTool(),
      linkDiagramTool: new LinkDiagramTool(),

      personStyleTool: new PersonStyleTool(),
      relationshipEditTool: new RelationshipEditTool(),
      schemaDrivenPersonTool: new SchemaDrivenPersonTool(),
      resetPersonStyleTool: new ResetPersonStyleTool(),
      cultureTagTool: new CultureTagTool(),
      healthTagTool: new HealthTagTool(),
      culturePaletteTool: new CulturePaletteTool(),

      addPersonTool: new AddPersonTool(),
      addPartnerTool: new AddPartnerTool(),
      addChildTool: new AddChildTool(),
      addParentTool: new AddParentTool(),
      addSiblingTool: new AddSiblingTool(),
      addChildToCoupleTool: new AddChildToCoupleTool(),
      groupAsSiblingsTool: new GroupAsSiblingsTool(),
      createRelationshipTool: new CreateRelationshipTool(),
    };
  }

  setFrameManager(frameManager: FrameManager): void {
    if (this.frameManager === frameManager) return;
    this.frameManager = frameManager;
    this.tools.frameTool.setFrameManager(frameManager);
    this.tools.universalDeleteTool.setFrameManager(frameManager);
  }

  async registerTools(toolRegistry: ToolRegistry): Promise<void> {
    const {
      colorTool,
      textTool,
      shapeTool,
      deleteTool,
      connectorTool,
      selectionTool,
      movementTool,
      frameTool,
      quickRelateTool,
      addParentsTool,
      shapePickerTool,
      shapeNotesTool,
      stickyNoteTool,
      stickyNotePropertiesTool,
      imagePropertiesTool,
      pdfPropertiesTool,
      universalTextTool,
      commentsTool,
      framesTool,
      penTool,
      universalDeleteTool,
      copyTool,
      connectTool,
      shapeSwitchTool,
      groupTool,
      shapeColorTool,
      shapeOutlineTool,
      fontSizeTool,
      fontPickerTool,
      textStyleTool,
      textAlignmentTool,
      textColorTool,
      imageReplaceTool,
      bpmnEventStyleTool,
      bpmnEventTriggerTool,
      bpmnGatewayTypeTool,
      bpmnActivityStyleTool,
      bpmnActivityMarkerTool,
      bpmnActivityTaskTool,
      linkDiagramTool,
      personStyleTool,
      relationshipEditTool,
      schemaDrivenPersonTool,
      resetPersonStyleTool,
      cultureTagTool,
      healthTagTool,
      culturePaletteTool,
      addPersonTool,
      addPartnerTool,
      addChildTool,
      addParentTool,
      addSiblingTool,
      addChildToCoupleTool,
      groupAsSiblingsTool,
      createRelationshipTool,
      tableGridTool,
      tableCellStyleTool,
    } = this.tools;

    // Register genogram-specific tools
    await toolRegistry.register(colorTool);
    // await toolRegistry.register(textTool);
    void textTool;
    await toolRegistry.register(shapeTool);
    await toolRegistry.register(deleteTool);
    await toolRegistry.register(connectorTool);
    await toolRegistry.register(selectionTool);
    await toolRegistry.register(movementTool);
    await toolRegistry.register(frameTool);
    await toolRegistry.register(quickRelateTool);

    // Register universal tools
    await toolRegistry.register(shapePickerTool);
    await toolRegistry.register(shapeNotesTool);
    await toolRegistry.register(stickyNoteTool);
    await toolRegistry.register(stickyNotePropertiesTool);
    await toolRegistry.register(imagePropertiesTool);
    await toolRegistry.register(pdfPropertiesTool);
    // Check if text tool is already registered before registering
    if (!toolRegistry.getTool('text')) {
      await toolRegistry.register(universalTextTool);
    }
    await toolRegistry.register(commentsTool);
    await toolRegistry.register(framesTool);
    await toolRegistry.register(penTool);

    // Register contextual tools
    await toolRegistry.register(universalDeleteTool);
    await toolRegistry.register(copyTool);
    await toolRegistry.register(connectTool);
    await toolRegistry.register(shapeSwitchTool);
    await toolRegistry.register(groupTool);
    await toolRegistry.register(shapeColorTool);
    await toolRegistry.register(shapeOutlineTool);
    await toolRegistry.register(fontSizeTool);
    await toolRegistry.register(fontPickerTool);
    await toolRegistry.register(textStyleTool);
    await toolRegistry.register(textAlignmentTool);
    await toolRegistry.register(textColorTool);
    await toolRegistry.register(imageReplaceTool);
    await toolRegistry.register(bpmnEventStyleTool);
    await toolRegistry.register(bpmnEventTriggerTool);
    await toolRegistry.register(bpmnGatewayTypeTool);
    await toolRegistry.register(bpmnActivityStyleTool);
    await toolRegistry.register(bpmnActivityMarkerTool);
    await toolRegistry.register(bpmnActivityTaskTool);
    // Register link-diagram once; guard against a second registration if another
    // pack already provided it (mirrors the universal text-tool guard above).
    if (!toolRegistry.getTool('link-diagram')) {
      await toolRegistry.register(linkDiagramTool);
    }

    // Register genogram-specific contextual tools
    await toolRegistry.register(personStyleTool);
    await toolRegistry.register(relationshipEditTool);
    await toolRegistry.register(schemaDrivenPersonTool);
    await toolRegistry.register(resetPersonStyleTool);
    await toolRegistry.register(cultureTagTool);
    await toolRegistry.register(healthTagTool);
    await toolRegistry.register(culturePaletteTool);
    // NOTE: GenogramFieldPropertiesTool removed - fields now unified in SchemaDrivenPersonTool

    // Register genogram quick action tools
    await toolRegistry.register(addPersonTool);
    await toolRegistry.register(addPartnerTool);
    await toolRegistry.register(addChildTool);
    await toolRegistry.register(addParentTool);
    await toolRegistry.register(addParentsTool);
    await toolRegistry.register(addSiblingTool);
    await toolRegistry.register(addChildToCoupleTool);
    await toolRegistry.register(groupAsSiblingsTool);
    await toolRegistry.register(createRelationshipTool);

    await toolRegistry.register(tableGridTool);
    await toolRegistry.register(tableCellStyleTool);
  }
}
